import { Injectable } from '@angular/core';
import { Observable, defer, delay, of } from 'rxjs';

import { HeatPoint } from '../../model/heat-point/heat-point';

export interface AccessPoint {
  x: number;
  y: number;
  power: number;
  sigma: number;
  x2?: number;
  y2?: number;
  height?: number;
}

export interface Obstacle {
  x: number;
  y: number;
  x2: number;
  y2: number;
  material: string;
  loss: number;
  thickness?: number;
  height: number;
}

export interface SyntheticGridConfig {
  width: number;
  height: number;
  cell: number;
  aps: AccessPoint[];
  obstacles: Obstacle[];
  noiseAmplitude?: number;
  observationHeight?: number;
}

export const DEFAULT_SYNTHETIC_GRID_CONFIG: SyntheticGridConfig = {
  width: 1280,
  height: 720,
  cell: 10,
  aps: [
    { x: 120, y: 100, x2: 260, y2: 120, power: 65, sigma: 220, height: 7 },
    { x: 420, y: 280, x2: 560, y2: 260, power: 55, sigma: 260, height: 7 },
    { x: 760, y: 200, x2: 900, y2: 200, power: 50, sigma: 300, height: 7 },
    { x: 1080, y: 320, x2: 1220, y2: 320, power: 58, sigma: 230, height: 7 },
    { x: 300, y: 580, x2: 440, y2: 600, power: 48, sigma: 280, height: 7 },
    { x: 820, y: 520, x2: 960, y2: 520, power: 54, sigma: 260, height: 7 },
    { x: 1160, y: 640, x2: 1280, y2: 660, power: 42, sigma: 320, height: 7 }
  ],
  obstacles: [
    { x: 620, y: 0, x2: 620, y2: 720, loss: 8, material: 'concrete', thickness: 40, height: 3.5 },
    { x: 0, y: 340, x2: 1280, y2: 340, loss: 6, material: 'drywall', thickness: 40, height: 2.8 },
    { x: 470, y: 120, x2: 470, y2: 520, loss: 4, material: 'glass', thickness: 30, height: 2.6 }
  ],
  noiseAmplitude: 5,
  observationHeight: 1
};

/**
 * Generatore sintentico di copertura RF. Riprende il modello dello script Python
 * originario ma produce i dati lato client, simulando la risposta di un web service.
 */
@Injectable({ providedIn: 'root' })
export class SyntheticGridService {

  /**
   * Espone i dati come Observable con latenza fittizia per imitare una chiamata HTTP.
   */
  fetchSyntheticGrid(
    config: SyntheticGridConfig = DEFAULT_SYNTHETIC_GRID_CONFIG,
    latencyMs = 400
  ): Observable<HeatPoint[]> {
    const normalized = this.normalizeConfig(config);
    return defer(() => of(this.generateGrid(normalized))).pipe(delay(latencyMs));
  }

  private generateGrid(config: SyntheticGridConfig): HeatPoint[] {
    const points: HeatPoint[] = [];
    const hasAps = config.aps.length > 0;
    for (let y = 0; y <= config.height; y += config.cell) {
      for (let x = 0; x <= config.width; x += config.cell) {
        const value = hasAps ? Math.round(this.interpolate(x, y, config) * 100) / 100 : 0;
        points.push({ x, y, value });
      }
    }
    return points;
  }

  private interpolate(x: number, y: number, config: SyntheticGridConfig): number {
    if (!config.aps.length) {
      return 0;
    }
    let bestSignal = Number.NEGATIVE_INFINITY;
    const observerHeight = config.observationHeight ?? 1;

    for (const ap of config.aps) {
      const samples = this.sampleAntenna(ap);
      let apSignal = 0;
      let activeSamples = 0;

      for (const sample of samples) {
        const { loss: obstacleLoss, blocked } = this.pathObstacleLoss(
          sample.x,
          sample.y,
          x,
          y,
          config.obstacles,
          ap.height ?? 7,
          observerHeight
        );
        if (blocked) {
          continue;
        }
        const dx = x - sample.x;
        const dy = y - sample.y;
        const distSq = dx * dx + dy * dy;
        const sigma = ap.sigma;
        const attenuation = Math.exp(-distSq / (2 * sigma * sigma));
        apSignal += Math.max(0, ap.power - obstacleLoss) * attenuation;
        activeSamples++;
      }

      if (activeSamples > 0) {
        apSignal /= activeSamples; // media lungo l'antenna per evitare picchi dipendenti dalla lunghezza
        bestSignal = Math.max(bestSignal, apSignal);
      }
    }

    if (!Number.isFinite(bestSignal)) {
      return 0;
    }

    let coverage = bestSignal;
    coverage += (Math.sin(x * 0.01) + Math.cos(y * 0.012)) * 6;
    coverage += this.randomNoise(config.noiseAmplitude ?? DEFAULT_SYNTHETIC_GRID_CONFIG.noiseAmplitude ?? 5);

    coverage = 20 + Math.max(0, Math.min(1, (coverage - 15) / 60)) * 70;
    return Math.max(5, Math.min(95, coverage));
  }

  private distanceToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    if (dx === 0 && dy === 0) {
      return Math.hypot(px - x1, py - y1);
    }
    const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)));
    const projX = x1 + t * dx;
    const projY = y1 + t * dy;
    return Math.hypot(px - projX, py - projY);
  }

  private pathObstacleLoss(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    obstacles: Obstacle[],
    txHeight: number,
    rxHeight: number
  ): { loss: number; blocked: boolean } {
    if (!obstacles?.length) {
      return { loss: 0, blocked: false };
    }
    const maxHeight = Math.max(1, ...obstacles.map((o) => o.height ?? 1), txHeight, rxHeight);
    const minLineHeight = Math.min(txHeight, rxHeight);
    let loss = 0;
    for (const obstacle of obstacles) {
      const thickness = obstacle.thickness ?? 30;
      const dist = this.segmentDistance(
        x1,
        y1,
        x2,
        y2,
        obstacle.x,
        obstacle.y,
        obstacle.x2,
        obstacle.y2
      );
      if (dist <= thickness / 2) {
        // Hard block for dense materials when the path crosses the obstacle footprint.
        const heightRatio = (obstacle.height ?? 1) / Math.max(maxHeight, 1);
        if ((obstacle.material === 'concrete' || obstacle.material === 'metal') && (obstacle.height ?? 0) >= minLineHeight) {
          return { loss: Number.POSITIVE_INFINITY, blocked: true };
        }
        const falloff = Math.max(0.25, 1 - dist / Math.max(thickness, 1));
        const heightFactor = Math.max(0.3, heightRatio);
        loss += obstacle.loss * falloff * heightFactor;
      }
    }
    return { loss, blocked: false };
  }

  private segmentDistance(
    ax: number,
    ay: number,
    bx: number,
    by: number,
    cx: number,
    cy: number,
    dx: number,
    dy: number
  ): number {
    const dist = this.distanceToSegment(ax, ay, cx, cy, dx, dy);
    const dist2 = this.distanceToSegment(bx, by, cx, cy, dx, dy);
    const inter = this.segmentsIntersect(ax, ay, bx, by, cx, cy, dx, dy);
    return inter ? 0 : Math.min(dist, dist2);
  }

  private segmentsIntersect(
    ax: number,
    ay: number,
    bx: number,
    by: number,
    cx: number,
    cy: number,
    dx: number,
    dy: number
  ): boolean {
    const orientation = (px: number, py: number, qx: number, qy: number, rx: number, ry: number) => {
      const val = (qy - py) * (rx - qx) - (qx - px) * (ry - qy);
      if (val === 0) return 0;
      return val > 0 ? 1 : 2;
    };
    const o1 = orientation(ax, ay, bx, by, cx, cy);
    const o2 = orientation(ax, ay, bx, by, dx, dy);
    const o3 = orientation(cx, cy, dx, dy, ax, ay);
    const o4 = orientation(cx, cy, dx, dy, bx, by);
    if (o1 !== o2 && o3 !== o4) {
      return true;
    }
    return false;
  }

  private randomNoise(amplitude: number): number {
    const span = Math.max(0, amplitude);
    return Math.random() * (span * 2) - span;
  }

  private sampleAntenna(ap: AccessPoint): { x: number; y: number }[] {
    const x2 = ap.x2 ?? ap.x;
    const y2 = ap.y2 ?? ap.y;
    const dx = x2 - ap.x;
    const dy = y2 - ap.y;
    const length = Math.hypot(dx, dy);
    if (length < 1) {
      return [{ x: ap.x, y: ap.y }];
    }
    // Spaziatura proporzionata a sigma per coprire uniformemente la linea.
    const spacing = Math.max(30, Math.min(ap.sigma / 2, 120));
    const segments = Math.max(1, Math.round(length / spacing));
    const samples: { x: number; y: number }[] = [];
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      samples.push({ x: ap.x + dx * t, y: ap.y + dy * t });
    }
    return samples;
  }

  private normalizeConfig(config: SyntheticGridConfig): SyntheticGridConfig {
    const fallback = DEFAULT_SYNTHETIC_GRID_CONFIG;
    const normalizedObstacles = this.mergeConnectedObstacles(
      [...(config.obstacles ?? fallback.obstacles ?? [])].map((o) => ({
        ...o,
        height: o.height ?? 3
      }))
    );
    return {
      width: config.width ?? fallback.width,
      height: config.height ?? fallback.height,
      cell: config.cell ?? fallback.cell,
      aps: [...config.aps],
      obstacles: normalizedObstacles,
      noiseAmplitude: config.noiseAmplitude ?? fallback.noiseAmplitude,
      observationHeight: config.observationHeight ?? fallback.observationHeight ?? 1
    };
  }

  private mergeConnectedObstacles(obstacles: Obstacle[]): Obstacle[] {
    const merged: Obstacle[] = [];
    const tolerance = 1e-3;
    const nearlyEqual = (a: number, b: number) => Math.abs(a - b) <= tolerance;

    const isSamePoint = (p1: { x: number; y: number }, p2: { x: number; y: number }) =>
      nearlyEqual(p1.x, p2.x) && nearlyEqual(p1.y, p2.y);

    const colinear = (a: Obstacle, b: Obstacle): boolean => {
      const ax = a.x2 - a.x;
      const ay = a.y2 - a.y;
      const bx = b.x2 - b.x;
      const by = b.y2 - b.y;
      return Math.abs(ax * by - ay * bx) <= tolerance;
    };

    const mergePair = (a: Obstacle, b: Obstacle): Obstacle => {
      const dirX = a.x2 - a.x;
      const dirY = a.y2 - a.y;
      const len = Math.hypot(dirX, dirY) || 1;
      const ux = dirX / len;
      const uy = dirY / len;
      const points = [
        { x: a.x, y: a.y },
        { x: a.x2, y: a.y2 },
        { x: b.x, y: b.y },
        { x: b.x2, y: b.y2 }
      ];
      const projections = points.map((p) => p.x * ux + p.y * uy);
      const minIdx = projections.indexOf(Math.min(...projections));
      const maxIdx = projections.indexOf(Math.max(...projections));
      const start = points[minIdx];
      const end = points[maxIdx];
      return {
        x: start.x,
        y: start.y,
        x2: end.x,
        y2: end.y,
        material: a.material,
        loss: Math.max(a.loss, b.loss),
        thickness: Math.max(a.thickness ?? 0, b.thickness ?? 0) || undefined,
        height: Math.max(a.height ?? 0, b.height ?? 0)
      };
    };

    const toProcess = [...obstacles];
    while (toProcess.length) {
      let current = toProcess.shift()!;
      let mergedAny = false;
      for (let i = 0; i < toProcess.length; i++) {
        const candidate = toProcess[i];
        const sharesVertex =
          isSamePoint({ x: current.x, y: current.y }, { x: candidate.x, y: candidate.y }) ||
          isSamePoint({ x: current.x, y: current.y }, { x: candidate.x2, y: candidate.y2 }) ||
          isSamePoint({ x: current.x2, y: current.y2 }, { x: candidate.x, y: candidate.y }) ||
          isSamePoint({ x: current.x2, y: current.y2 }, { x: candidate.x2, y: candidate.y2 });
        if (sharesVertex && colinear(current, candidate)) {
          current = mergePair(current, candidate);
          toProcess.splice(i, 1);
          mergedAny = true;
          i = -1; // restart scanning list
        }
      }
      merged.push(current);
      if (mergedAny) {
        // Continue loop to catch further merges with new segments in merged array
        const last = merged.pop()!;
        toProcess.unshift(last);
      }
    }
    return merged;
  }
}
